/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  IconChevronDown,
  IconAlignBoxBottomCenter,
  IconAlignBoxBottomLeft,
  IconAlignBoxBottomRight,
  IconAlignBoxCenterBottom,
  IconAlignBoxCenterTop,
  IconAlignBoxLeftMiddle,
  IconShare,
} from "@tabler/icons-react";
import { useSearchParams } from "react-router-dom";
import RoleToggle from "../../../components/RoleToggle";
import toast from "react-hot-toast";

import {
  useFetchCustomization,
  useSaveCustomization,
} from "../../../apis/marketPlaceApi";
import { useLanguage } from "../../../context/LanguageContext";

const roles = ["App", "Web"];

const Toggle = ({ enabled, onChange }: any) => (
  <div
    onClick={onChange}
    className={`w-14 h-7 flex items-center rounded-full p-1 cursor-pointer transition ${
      enabled ? "bg-[#FFC90D]" : "bg-gray-300"
    }`}
  >
    <div
      className={`bg-white w-5 h-5 rounded-full shadow transition-transform duration-200 ${
        enabled ? "translate-x-7" : ""
      }`}
    />
  </div>
);

const icons = [
  IconAlignBoxBottomLeft,
  IconAlignBoxBottomCenter,
  IconAlignBoxBottomRight,
  IconAlignBoxCenterBottom,
];

const IconSelector = ({ selected, onChange }: any) => (
  <div className="bg-[#F7F6F2] rounded-full px-2 py-1 flex gap-2">
    {icons.map((Icon, i) => (
      <button
        key={i}
        onClick={() => onChange(i)}
        className={`p-2 rounded-full transition ${
          selected === i
            ? "bg-white shadow-sm"
            : "text-gray-600 hover:bg-gray-100"
        }`}
      >
        <Icon size={20} />
      </button>
    ))}
  </div>
);

const Section = ({
  title,
  children,
  toggle,
  right,
  enabled,
  onToggle,
}: any) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
    <div className="flex justify-between items-center">
      <span className="text-base font-medium text-black">{title}</span>
      {toggle && <Toggle enabled={enabled} onChange={onToggle} />}
      {right}
    </div>
    {children}
  </div>
);

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

const layoutMap = ["left", "center", "right", "full"];
const imageMap = ["left", "center", "right", "full"];
const buttonMap = ["left", "center", "right", "full"];
const ratioMap = ["1:1", "4:3", "16:9", "original"];

const MarketplaceTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeRole = searchParams.get("sub") || "App";
  const currentTab = searchParams.get("tab") || "App";
  const platform = activeRole === "App" ? "app" : "web";
  const { t } = useLanguage();

  const { data } = useFetchCustomization(platform);
  const saveMutation = useSaveCustomization(platform);

  const [form, setForm] = useState<any>({
    layoutType: 0,
    imageStyle: 0,
    imageRatio: 0,
    buttonStyle: 0,
    title: true,
    price: true,
    description: true,
    quantitySelector: true,
    cartEnabled: true,
    buyNowEnabled: true,
    taxIndication: true,
    moreInformation: true,
    customerRating: true,
    customerShare: true,
    variantsEnabled: true,
    showVariants: true,
    termCondition: true,
    layoutEnabled: true,
    imageEnabled: true,
    customerEnabled: true,
    specificationPosition: "bottom",
    extraImages: [],
    mainImage: "",
    variants: [],
    options: [],
  });

  const updateField = (key: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleRoleChange = (role: string) => {
    setSearchParams({ tab: currentTab, sub: role });
  };

  const safeIndex = (map: string[], value?: string) => {
    const i = map.indexOf(value || "");
    return i === -1 ? 0 : i;
  };

  useEffect(() => {
    if (!data) return;

    setForm({
      ...data,

      extraImages: data.extraImages || [],
      variants: data.variants || [],
      options: data.options || [],

      layoutType: safeIndex(layoutMap, data.layoutType),
      imageStyle: safeIndex(imageMap, data.imageStyle),
      imageRatio: safeIndex(ratioMap, data.imageRatio),
      buttonStyle: safeIndex(buttonMap, data.buttonStyle),

      variantsEnabled: data.showVariants,
    });
  }, [data]);

  const handleSave = () => {
    const payload = {
      termCondition: form.termCondition,
      layoutEnabled: form.layoutEnabled,
      imageEnabled: form.imageEnabled,
      customerRating: form.customerRating,
      customerShare: form.customerShare,
      showVariants: form.variantsEnabled,
      title: form.title,
      price: form.price,
      description: form.description,
      quantitySelector: form.quantitySelector,
      cartEnabled: form.cartEnabled,
      buyNowEnabled: form.buyNowEnabled,
      taxIndication: form.taxIndication,
      moreInformation: form.moreInformation,

      layoutType: layoutMap[form.layoutType],
      imageStyle: imageMap[form.imageStyle],
      imageRatio: ratioMap[form.imageRatio],
      buttonStyle: buttonMap[form.buttonStyle],
      specificationPosition: form.specificationPosition || "bottom",

      mainImage: form.mainImage || null,
      extraImages: form.extraImages || [],

      variants: form.variants || [],
      options: form.options || [],
      customerEnabled: form.customerEnabled || true,
    };

    saveMutation.mutate(payload, {
      onSuccess: () => toast.success(t("Customization Saved")),
      onError: () => toast.error(t("Failed to Save")),
    });
  };

  const getImageRatioClass = () => {
    switch (form.imageRatio) {
      case 0:
        return "aspect-square";
      case 1:
        return "aspect-[4/3]";
      case 2:
        return "aspect-[16/9]";
      case 3:
        return "aspect-auto";
      default:
        return "aspect-square";
    }
  };

  const getTextAlignClass = () => {
    switch (form.layoutType) {
      case 0:
        return "text-left";
      case 1:
        return "text-center";
      case 2:
        return "text-right";
      case 3:
        return "text-center";
      default:
        return "text-center";
    }
  };

  const getFlexAlignClass = () => {
    switch (form.layoutType) {
      case 0:
        return "items-start";
      case 1:
        return "items-center";
      case 2:
        return "items-end";
      case 3:
        return "items-center";
      default:
        return "items-center";
    }
  };

  const getJustifyAlignClass = () => {
    switch (form.layoutType) {
      case 0:
        return "justify-start";
      case 1:
        return "justify-center";
      case 2:
        return "justify-end";
      case 3:
        return "justify-center";
      default:
        return "justify-center";
    }
  };
  const getImageStyleClass = () => {
    switch (form.imageStyle) {
      case 0:
        return "justify-start";
      case 1:
        return "justify-center";
      case 2:
        return "justify-end";
      case 3:
        return "w-full justify-center";
      default:
        return "justify-center";
    }
  };

  const getButtonStyleClass = () => {
    switch (form.buttonStyle) {
      case 0:
        return "justify-start";
      case 1:
        return "justify-center";
      case 2:
        return "justify-end";
      case 3:
        return "justify-stretch";
      default:
        return "justify-center";
    }
  };

  return (
    <div className="space-y-4 border border-[#d2d2d2] rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5]">
      <div className="flex justify-between items-center mb-4">
        <RoleToggle
          options={roles.map((r) => t(r))}
          active={t(activeRole)}
          onChange={(role: string) => {
            const orig = role === t("App") ? "App" : "Web";
            handleRoleChange(orig);
          }}
        />

        <button
          onClick={handleSave}
          className="px-4 py-2 bg-[#FBD037] text-[#212121] rounded-full"
        >
          {saveMutation.isPending ? t("Saving...") : t("Save")}
        </button>
      </div>

      <div className="flex gap-6">
        {activeRole === "App" && (
          <div className="w-1/2 flex justify-center">
            <div className="w-[375px] h-[712px] bg-black rounded-[40px] p-3 shadow-2xl relative">
              <div className="h-full w-full rounded-[30px] bg-white overflow-y-auto hide-scrollbar transition-all duration-300">
                {form.imageEnabled && (
                  <div className="flex flex-col px-4 pt-4 space-y-3">
                    <div
                      className={`flex flex-col items-center ${getJustifyAlignClass()}`}
                    >
                      <div
                        className={`w-full ${getImageRatioClass()} bg-gray-100 rounded-xl shadow flex items-center justify-center overflow-hidden`}
                      >
                        {form.mainImage ? (
                          form.mainImage.startsWith("data:video") ? (
                            <video
                              src={form.mainImage}
                              controls
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <img
                              src={form.mainImage}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          )
                        ) : (
                          <span className="text-gray-400 text-sm">
                            {t("Main Image")}
                          </span>
                        )}
                      </div>

                      <div
                        className={`flex gap-2 mt-3 ${getJustifyAlignClass()}`}
                      >
                        {(form.mainImage || form.extraImages?.length > 0) && (
                          <>
                            {form.mainImage && (
                              <div className="w-2 h-2 rounded-full bg-black" />
                            )}

                            {form.extraImages?.map((_: string, i: number) => (
                              <div
                                key={i}
                                className="w-2 h-2 rounded-full bg-gray-300"
                              />
                            ))}

                            {!form.mainImage &&
                              form.extraImages?.length === 0 && (
                                <>
                                  <div className="w-2 h-2 rounded-full bg-black" />
                                  <div className="w-2 h-2 rounded-full bg-gray-300" />
                                  <div className="w-2 h-2 rounded-full bg-gray-300" />
                                </>
                              )}
                          </>
                        )}
                      </div>
                    </div>

                    {form.extraImages?.length > 0 && (
                      <div
                        className={`w-full border-t pt-3 ${getImageStyleClass()}`}
                      >
                        <p className="text-xs text-gray-500 mb-2 px-1">
                          {form.extraImages.length} {t(form.extraImages.length !== 1 ? "more photos" : "more photo")}
                        </p>
                        <div className="flex gap-2 overflow-x-auto pb-2 px-1 hide-scrollbar">
                         {form.extraImages.map((img: string, i: number) => (
  <div key={i} className="flex-shrink-0 relative group">
    {img.startsWith("data:video") ? (
      <video
        src={img}
        className="w-16 h-16 object-cover rounded border border-gray-300 cursor-pointer"
      />
    ) : (
      <img
        src={img}
        alt={`Extra ${i + 1}`}
        className="w-16 h-16 object-cover rounded border border-gray-300 cursor-pointer"
      />
    )}
    <span className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded transition" />
  </div>
))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div
                  className={`px-5 pb-6 space-y-3 flex flex-col ${getFlexAlignClass()} transition-all duration-300`}
                >
                  {form.title && (
                    <div className={`w-full ${getTextAlignClass()}`}>
                      <h1 className="text-lg font-semibold text-gray-900">
                        {t("Premium Wireless Headphones")}
                      </h1>
                      <p className="text-sm text-gray-500 mt-1">
                        {t("Noise Cancellation | 30h Battery")}
                      </p>
                    </div>
                  )}

                  {
                    <div
                      className={`w-full flex justify-between items-center gap-2 ${getJustifyAlignClass()}`}
                    >
                      {form.customerRating && (
                        <div
                          className={`text-sm font-medium text-green-700 ${getTextAlignClass()}`}
                        >
                          {t("★ 4.8 | 128 Ratings")}
                        </div>
                      )}
                      {form.customerShare && (
                        <div
                          className={`text-sm text-[#FFC90D] border border-[#D2D2D2] rounded cursor-pointer ${getTextAlignClass()}`}
                        >
                          <IconShare />
                        </div>
                      )}
                    </div>
                  }

                  {form.price && (
                    <div className={`w-full ${getTextAlignClass()}`}>
                      <span className="text-2xl font-bold text-gray-900">
                        ₹1,999
                      </span>
                      <span className="ml-2 text-sm text-gray-500 line-through">
                        ₹2,999
                      </span>
                      <span className="ml-2 text-sm text-green-600 font-medium">
                        {t("33% OFF")}
                      </span>
                    </div>
                  )}
                  {form.taxIndication && (
                    <p className="text-green-600 text-sm">
                      {t("Inclusive of all taxes")}
                    </p>
                  )}

                  {form.variantsEnabled && (
                    <div className={`w-full space-y-4 ${getTextAlignClass()}`}>
                      <div>
                        <p className="text-sm font-medium mb-2">{t("Select Color")}</p>
                        <div
                          className={`flex gap-3  w-full ${getJustifyAlignClass()}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-black border-2 border-black cursor-pointer hover:scale-105 transition" />
                          <div className="w-8 h-8 rounded-full bg-white border cursor-pointer hover:scale-105 transition" />
                          <div className="w-8 h-8 rounded-full bg-gray-400 border cursor-pointer hover:scale-105 transition" />
                        </div>
                      </div>

                      <div>
                        <p className="text-sm font-medium mb-2">{t("Select Size")}</p>
                        <div
                          className={`flex gap-2 flex-wrap  w-full ${getJustifyAlignClass()}`}
                        >
                          {["S", "M", "L", "XL"].map((size) => (
                            <button
                              key={size}
                              className="px-4 py-2 border rounded-md text-sm hover:border-black hover:bg-gray-50 transition"
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {form.description && (
                    <p
                      className={`text-sm text-gray-600 leading-relaxed ${getTextAlignClass()}`}
                    >
                      {t("Premium wireless headphones with active noise cancellation, 30-hour battery life, and crystal-clear sound quality.")}
                    </p>
                  )}

                  {form.quantitySelector && (
                    <div
                      className={`flex gap-4 ${getJustifyAlignClass()} items-center`}
                    >
                      <button className="w-9 h-9 border rounded-md text-lg hover:bg-gray-100 transition">
                        -
                      </button>
                      <span className="text-lg font-semibold">1</span>
                      <button className="w-9 h-9 border rounded-md text-lg hover:bg-gray-100 transition">
                        +
                      </button>
                    </div>
                  )}
                </div>

                <div className="sticky bottom-0 bg-white border-t p-4 space-y-3">
                  <div
                    className={`flex ${form.buttonStyle === 3 ? "flex-col gap-2" : "gap-3"} ${getButtonStyleClass()}`}
                  >
                    {form.cartEnabled && (
                      <button
                        className={`${form.buttonStyle === 3 ? "w-full" : "flex-1"} py-3 bg-[#FFC90D] text-white font-semibold rounded-md hover:bg-[#e6b800] transition`}
                      >
                        {t("Add to Cart")}
                      </button>
                    )}
                    {form.buyNowEnabled && (
                      <button
                        className={`${form.buttonStyle === 3 ? "w-full" : "flex-1"} py-3 border border-[#FFC90D] text-[#FFC90D] font-semibold rounded-md hover:bg-[#FFC90D]/10 transition`}
                      >
                        {t("Buy Now")}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-5 bg-gray-900 rounded-full" />
            </div>
          </div>
        )}

        {/* WEB */}

        {activeRole === "Web" && (
          <div className="w-full bg-[#FCFDF8] border border-[#D2D2D2] rounded-2xl p-8 shadow-inner">
            <div className="max-w-6xl mx-auto grid grid-cols-2 gap-10">
              {form.imageEnabled && (
                <div className="space-y-4">
                  <div
                    className={`w-full ${getImageRatioClass()} bg-white rounded-xl shadow flex items-center justify-center text-gray-500 text-xl font-medium overflow-hidden`}
                  >
                    {form.mainImage ? (
                      <img
                        src={form.mainImage}
                        alt="Main"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      t("Main Product Image")
                    )}
                  </div>

                  <div
                    className={`flex gap-3 flex-wrap ${getImageStyleClass()}`}
                  >
                    {form.mainImage && (
                      <img
                        src={form.mainImage}
                        alt="Main"
                        onClick={() => {}}
                        className="w-20 h-20 object-cover rounded border border-black cursor-pointer"
                      />
                    )}
                    {form.extraImages?.length > 0 &&
                      form.extraImages.map((img: string, i: number) => (
                        <img
                          key={i}
                          src={img}
                          onClick={() => updateField("mainImage", img)}
                          alt={`Extra ${i + 1}`}
                          className={`w-20 h-20 object-cover rounded border cursor-pointer ${
                            form.mainImage === img
                              ? "border-black"
                              : "border-gray-300"
                          }`}
                        />
                      ))}
                  </div>
                </div>
              )}

              <div className={`flex flex-col gap-6 ${getTextAlignClass()}`}>
                {form.title && (
                  <div>
                    <h1 className="text-3xl font-semibold text-gray-900">
                      {t("Premium Wireless Headphones")}
                    </h1>
                    <p className="text-gray-500 mt-1">
                      {t("Noise Cancellation | 30h Battery Life")}
                    </p>
                  </div>
                )}

                {form.customerRating && (
                  <div className="text-green-700 font-medium">
                    {t("★ 4.8 | 128 Ratings")}
                  </div>
                )}

                {form.price && (
                  <div>
                    <span className="text-3xl font-bold text-gray-900">
                      ₹1,999
                    </span>
                    <span className="ml-3 text-gray-500 line-through">
                      ₹2,999
                    </span>
                    <span className="ml-3 text-green-600 font-medium">
                      {t("33% OFF")}
                    </span>
                  </div>
                )}

                {form.variantsEnabled && (
                  <div className="space-y-5">
                    <div>
                      <p className="font-medium mb-2">{t("Select Color")}</p>
                      <div
                        className={`flex gap-3  w-full ${getJustifyAlignClass()}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-black border-2 border-black cursor-pointer" />
                        <div className="w-10 h-10 rounded-full bg-white border cursor-pointer" />
                        <div className="w-10 h-10 rounded-full bg-gray-400 border cursor-pointer" />
                      </div>
                    </div>

                    <div>
                      <p className="font-medium mb-2">{t("Select Size")}</p>
                      <div
                        className={`flex gap-2 flex-wrap  w-full ${getJustifyAlignClass()}`}
                      >
                        {["S", "M", "L", "XL"].map((size) => (
                          <button
                            key={size}
                            className="px-5 py-2 border rounded-md hover:border-black transition"
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {form.description && (
                  <p className="text-gray-600 leading-relaxed">
                    {t("Premium wireless headphones with active noise cancellation, 30-hour battery life, and crystal-clear sound quality.")}
                  </p>
                )}

                {form.quantitySelector && (
                  <div
                    className={`flex gap-4 ${getJustifyAlignClass()} items-center`}
                  >
                    <button className="w-10 h-10 border rounded hover:bg-gray-100">
                      -
                    </button>
                    <span className="text-lg font-semibold">1</span>
                    <button className="w-10 h-10 border rounded hover:bg-gray-100">
                      +
                    </button>
                  </div>
                )}

                <div
                  className={`${form.buttonStyle === 3 ? "flex flex-col gap-2" : "flex gap-4"} pt-4 ${getButtonStyleClass()}`}
                >
                  {form.cartEnabled && (
                    <button
                      className={`${form.buttonStyle === 3 ? "w-full" : "flex-1"} py-4 bg-[#FFC90D] text-white font-semibold rounded-lg hover:bg-[#e6b800] transition`}
                    >
                      {t("Add to Cart")}
                    </button>
                  )}

                  {form.buyNowEnabled && (
                    <button
                      className={`${form.buttonStyle === 3 ? "w-full" : "flex-1"} py-4 border border-[#FFC90D] text-[#FFC90D] font-semibold rounded-lg hover:bg-[#FFF8E1] transition`}
                    >
                      {t("Buy Now")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="w-1/2 bg-[#FCFDF8] rounded-2xl shadow-lg">
          <div className="border-b px-6 py-4 flex justify-between items-center">
            <h2 className="text-2xl font-medium">{t("Customize Product Page")}</h2>
            <IconChevronDown />
          </div>

          <div className="space-y-4 h-[680px] p-6 overflow-y-auto">
            <Section
              title={t("Term & Condition")}
              toggle
              enabled={form.termCondition}
              onToggle={() => updateField("termCondition", !form.termCondition)}
            />

            <Section
              title={t("Layout")}
              toggle
              enabled={form.layoutEnabled}
              onToggle={() => updateField("layoutEnabled", !form.layoutEnabled)}
            >
              <div className="flex justify-between items-center">
                <span>{t("Layout Style")}</span>
                <IconSelector
                  selected={form.layoutType}
                  onChange={(value: any) => updateField("layoutType", value)}
                />
              </div>
            </Section>

            <Section
              title={t("Image")}
              toggle
              enabled={form.imageEnabled}
              onToggle={() => updateField("imageEnabled", !form.imageEnabled)}
            >
              <div className="flex justify-between items-center">
                <span>{t("Image Style")}</span>
                <IconSelector
                  selected={form.imageStyle}
                  onChange={(value: any) => updateField("imageStyle", value)}
                />
              </div>

              <div className="flex justify-between items-center">
                <span>{t("Image Ratio")}</span>

                <select
                  value={form.imageRatio}
                  onChange={(e) =>
                    updateField("imageRatio", Number(e.target.value))
                  }
                  className="bg-[#F7F6F2] rounded-full px-4 py-2 text-gray-600"
                >
                  {ratioMap.map((r, i) => (
                    <option key={r} value={i}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-between items-center">
                <span>{t("Main Image")}</span>

                <input
                  id="mainImageInput"
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    try {
                      const base64 = await fileToBase64(file);
                      updateField("mainImage", base64);
                    } catch (error) {
                      console.error("Error converting image:", error);
                    }
                  }}
                />

                <button
                  onClick={() =>
                    document.getElementById("mainImageInput")?.click()
                  }
                  className="bg-[#FFC90D] hover:bg-[#e6b800] text-black px-8 py-2 rounded-full font-medium transition"
                >
                  {t("Replace")}
                </button>
              </div>

              {form.mainImage && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">{t("Current:")}</span>
                  <img
                    src={form.mainImage}
                    alt="Main"
                    className="w-20 h-20 object-cover rounded border border-gray-300"
                  />
                </div>
              )}
              <div className="flex justify-between items-center">
                <span>{t("Extra Images")}</span>
                <div className="bg-[#F7F6F2] rounded-full flex items-center px-1 py-1 gap-2">
                  <button
                    onClick={() =>
                      document.getElementById("extraImageInput")?.click()
                    }
                    className="bg-[#FFC90D] hover:bg-[#e6b800] px-6 py-2 rounded-full font-medium transition"
                  >
                    {t("Add")}
                  </button>
                  {form.extraImages?.length > 0 && (
                    <button
                      onClick={() => updateField("extraImages", [])}
                      className="text-red-500 px-4 font-medium"
                    >
                      {t("Remove All")}
                    </button>
                  )}
                </div>
              </div>

              {form.extraImages?.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {form.extraImages.map((img: string, i: number) => (
                    <div key={i} className="relative">
                      {img.startsWith("data:video") ? (
                        <video
                          src={img}
                          className="w-16 h-16 object-cover rounded border border-gray-300"
                        />
                      ) : (
                        <img
                          src={img}
                          alt={`Extra ${i + 1}`}
                          className="w-16 h-16 object-cover rounded border border-gray-300"
                        />
                      )}
                      <button
                        onClick={() =>
                          updateField(
                            "extraImages",
                            form.extraImages.filter(
                              (_: string, idx: number) => idx !== i,
                            ),
                          )
                        }
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section
              title={t("Customer")}
              className="flex justify-between items-center"
            >
              <div className="flex gap-4">
                <div className="bg-[#F7F6F2] rounded-full flex items-center pl-6 px-1 py-1 gap-2">
                  <span>{t("Rating")}</span>
                  <Toggle
                    enabled={form.customerRating}
                    onChange={() =>
                      updateField("customerRating", !form.customerRating)
                    }
                  />
                </div>
                <div className="bg-[#F7F6F2] rounded-full flex items-center pl-6 px-1 py-1 gap-2">
                  <span>{t("Share")}</span>
                  <Toggle
                    enabled={form.customerShare}
                    onChange={() =>
                      updateField("customerShare", !form.customerShare)
                    }
                  />
                </div>
              </div>
            </Section>

            <Section
              title={t("Show Variants option and Selection")}
              toggle
              enabled={form.variantsEnabled}
              onToggle={() =>
                updateField("variantsEnabled", !form.variantsEnabled)
              }
            />

            <Section
              title={t("Title")}
              toggle
              enabled={form.title}
              onToggle={() => updateField("title", !form.title)}
            />

            <Section
              title={t("Price")}
              toggle
              enabled={form.price}
              onToggle={() => updateField("price", !form.price)}
            />

            <Section
              title={t("Description")}
              toggle
              enabled={form.description}
              onToggle={() => updateField("description", !form.description)}
            />

            <Section
              title={t("Select Quantity")}
              toggle
              enabled={form.quantitySelector}
              onToggle={() =>
                updateField("quantitySelector", !form.quantitySelector)
              }
            />

            <Section
              title={t("Cart")}
              right={
                <div className="bg-[#F7F6F2] rounded-full flex items-center pl-6 px-1 py-1 gap-2">
                  <span>{t("Buy Now")}</span>
                  <Toggle
                    enabled={form.buyNowEnabled}
                    onChange={() =>
                      updateField("buyNowEnabled", !form.buyNowEnabled)
                    }
                  />
                </div>
              }
              toggle
              enabled={form.cartEnabled}
              onToggle={() => updateField("cartEnabled", !form.cartEnabled)}
            >
              <div className="flex justify-between items-center">
                <span>{t("Button Style")}</span>
                <IconSelector
                  selected={form.buttonStyle}
                  onChange={(value: any) => updateField("buttonStyle", value)}
                />
              </div>
            </Section>

            <Section
              title={t("Tax Indication")}
              toggle
              enabled={form.taxIndication}
              onToggle={() => updateField("taxIndication", !form.taxIndication)}
            />

            <Section
              title={t("More Information")}
              toggle
              enabled={form.moreInformation}
              onToggle={() =>
                updateField("moreInformation", !form.moreInformation)
              }
            />

            {/* <Section
              title="Specification"
              className="flex justify-between items-center"
            >
              <select className="bg-[#F7F6F2] rounded-full px-3 py-2">
                <option>Bottom of Pages</option>
              </select>
            </Section> */}
          </div>
        </div>
      </div>

      <input
        id="extraImageInput"
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={async (e) => {
          const files = e.target.files;
          if (!files) return;

          try {
            const mediaFiles = await Promise.all(
              Array.from(files).map(async (file) => {
                const base64 = await fileToBase64(file);
                return base64;
              }),
            );

            updateField("extraImages", [...form.extraImages, ...mediaFiles]);
          } catch (error) {
            console.error("Error converting images:", error);
          }
        }}
      />
    </div>
  );
};

export default MarketplaceTab;
